import { appstr } from "@coreapp/utils/appstr";

export class ChartDiagnostics {
  constructor(chartConfig) {
    this.chartConfig = chartConfig;
    this.syncData = chartConfig?.sync;
    this.diagnostics = this.syncData?.diagnostics;
  }

  hasDiagnostics() {
    return this.diagnostics !== null && this.diagnostics !== undefined;
  }

  getMainDiagnostic() {
    if (!this.hasDiagnostics()) return null;

    const mainDiag = this.diagnostics.main;
    if (!mainDiag) return null;

    if (Array.isArray(mainDiag)) {
      const failedDiag = mainDiag.find(
        diag => diag && (diag.success === false || diag.success === null)
      );
      return failedDiag || null;
    } else {
      return mainDiag && (mainDiag.success === false || mainDiag.success === null)
        ? mainDiag
        : null;
    }
  }

  getUploaderDiagnostic() {
    if (!this.hasDiagnostics()) return null;

    const uploaderDiag = this.diagnostics.uploader;
    if (!uploaderDiag) return null;

    if (Array.isArray(uploaderDiag)) {
      const failedDiag = uploaderDiag.find(
        diag => diag && (diag.success === false || diag.success === null)
      );
      return failedDiag || null;
    } else {
      return uploaderDiag && (uploaderDiag.success === false || uploaderDiag.success === null)
        ? uploaderDiag
        : null;
    }
  }

  getPhotoDiagnostic() {
    if (!this.hasDiagnostics()) return null;

    // Deliberately not gated on import.photo.photoSource: that is only settable
    // for the few connectors with dedicated photo config, while most charts pull
    // photos from a mapped URL/HRIS field. The backend drops this outcome when a
    // save or refresh leaves the chart with no photo key, so its presence is the signal.
    const photoDiag = this.diagnostics.photo?.import;
    if (!photoDiag) return null;

    return photoDiag.success === false || photoDiag.success === null ? photoDiag : null;
  }

  getAppendDiagnostic(index = null) {
    if (!this.hasDiagnostics() || !this.diagnostics.append) return null;

    if (index !== null) {
      const appendDiag = this.diagnostics.append[index];
      return appendDiag && (appendDiag.success === false || appendDiag.success === null)
        ? appendDiag
        : null;
    }

    return this.diagnostics.append;
  }

  getAppendDiagnosticByConfigIndex(configIndex) {
    if (!this.hasDiagnostics() || !this.diagnostics.append) return null;

    // Try to find diagnostic by configIndex first
    let diagnostic = this.diagnostics.append.find(diag => diag.configIndex === configIndex);

    // Fallback to array index for backward compatibility
    if (!diagnostic && configIndex < this.diagnostics.append.length) {
      diagnostic = this.diagnostics.append[configIndex];
    }

    return diagnostic && (diagnostic.success === false || diagnostic.success === null)
      ? diagnostic
      : null;
  }

  getMergeDiagnostic(index = null) {
    if (!this.hasDiagnostics() || !this.diagnostics.merge) return null;

    if (index !== null) {
      const mergeDiag = this.diagnostics.merge[index];
      return mergeDiag && (mergeDiag.success === false || mergeDiag.success === null)
        ? mergeDiag
        : null;
    }

    return this.diagnostics.merge;
  }

  getMergeDiagnosticByConfigIndex(configIndex) {
    if (!this.hasDiagnostics() || !this.diagnostics.merge) return null;

    // Try to find diagnostic by configIndex first
    let diagnostic = this.diagnostics.merge.find(diag => diag.configIndex === configIndex);

    // Fallback to array index for backward compatibility
    if (!diagnostic && configIndex < this.diagnostics.merge.length) {
      diagnostic = this.diagnostics.merge[configIndex];
    }

    return diagnostic && (diagnostic.success === false || diagnostic.success === null)
      ? diagnostic
      : null;
  }

  getOverallDiagnostic() {
    if (!this.hasDiagnostics()) return null;

    const mainDiag = this.getMainDiagnostic();
    const uploaderDiag = this.getUploaderDiagnostic();

    return mainDiag || uploaderDiag;
  }

  getAllFailures() {
    const failures = [];

    const overallDiag = this.getOverallDiagnostic();
    if (overallDiag) {
      failures.push({
        type: "main",
        message: overallDiag.message,
        success: overallDiag.success
      });
    }

    if (this.diagnostics?.append) {
      this.diagnostics.append.forEach((appendDiag, index) => {
        if (appendDiag && (appendDiag.success === false || appendDiag.success === null)) {
          failures.push({
            type: "append",
            index,
            configIndex: appendDiag.configIndex,
            message: appendDiag.message,
            success: appendDiag.success
          });
        }
      });
    }

    if (this.diagnostics?.merge) {
      this.diagnostics.merge.forEach((mergeDiag, index) => {
        if (mergeDiag && (mergeDiag.success === false || mergeDiag.success === null)) {
          failures.push({
            type: "merge",
            index,
            configIndex: mergeDiag.configIndex,
            message: mergeDiag.message,
            success: mergeDiag.success
          });
        }
      });
    }

    const photoDiag = this.getPhotoDiagnostic();
    if (photoDiag) {
      failures.push({
        type: "photo",
        message: photoDiag.message,
        success: photoDiag.success
      });
    }

    return failures;
  }

  hasAnyFailures() {
    return this.getAllFailures().length > 0;
  }

  getDiagnosticByConfigIndex(type, configIndex) {
    if (type === "import" || type === "main") {
      return this.getOverallDiagnostic();
    } else if (type === "append") {
      return this.getAppendDiagnosticByConfigIndex(configIndex);
    } else if (type === "merge") {
      return this.getMergeDiagnosticByConfigIndex(configIndex);
    }
    return null;
  }

  getFailureMessage(type, index = null) {
    switch (type) {
      case "import": // import section maps to main diagnostics
      case "main":
        return this.getOverallDiagnostic()?.message || "";
      case "append":
        return this.getAppendDiagnostic(index)?.message || "";
      case "merge":
        return this.getMergeDiagnostic(index)?.message || "";
      default:
        return "";
    }
  }

  getFailureMessageByConfigIndex(type, configIndex) {
    return this.getDiagnosticByConfigIndex(type, configIndex)?.message || "";
  }

  getFailureTooltip(type, index = null) {
    const message = this.getFailureMessage(type, index);
    if (!message) return "";

    const prefix =
      type === "main" || type === "import"
        ? appstr("Data Load Failure")
        : type === "append"
          ? appstr("Append Data Failure")
          : appstr("Merge Data Failure");

    return `${prefix}: ${message}`;
  }

  getFailureTooltipByConfigIndex(type, configIndex) {
    const message = this.getFailureMessageByConfigIndex(type, configIndex);
    if (!message) return "";

    const prefix =
      type === "main" || type === "import"
        ? appstr("Data Load Failure")
        : type === "append"
          ? appstr("Append Data Failure")
          : appstr("Merge Data Failure");

    return `${prefix}: ${message}`;
  }

  getSourceName(type, index = null) {
    switch (type) {
      case "import":
      case "main":
        return this.chartConfig?.import?.source || appstr("Unknown");
      case "append":
        return this.chartConfig?.append?.[index]?.source || appstr("Unknown");
      case "merge":
        return this.chartConfig?.merge?.[index]?.source || appstr("Unknown");
      case "photo":
        return (
          this.chartConfig?.import?.photo?.photoSource ||
          this.diagnostics?.photo?.import?.connectors?.join(", ") ||
          appstr("Unknown")
        );
      default:
        return appstr("Unknown");
    }
  }

  getSourceLabel(type) {
    switch (type) {
      case "import":
      case "main":
        return appstr("Main data source");
      case "append":
        return appstr("Append data source");
      case "merge":
        return appstr("Merge data source");
      case "photo":
        return appstr("Photo data source");
      default:
        return appstr("Data source");
    }
  }

  getFailuresList() {
    const failures = this.getAllFailures();
    return failures.map(failure => {
      // Use configIndex if available, otherwise fallback to array index for backward compatibility
      const indexToUse = failure.configIndex !== undefined ? failure.configIndex : failure.index;
      const sourceLabel = this.getSourceLabel(failure.type);
      const sourceName = this.getSourceName(failure.type, indexToUse);
      // Photo rows lead with the message: naming the connector says nothing about
      // whether photos partially imported, wholly failed, or were never set up,
      // which is the only reason this row exists.
      const displayText =
        failure.type === "photo" && failure.message
          ? `${sourceLabel}: ${failure.message}`
          : `${sourceLabel}: ${sourceName}`;
      return {
        ...failure,
        sourceName,
        sourceLabel,
        displayText
      };
    });
  }

  getFailuresCount() {
    return this.getAllFailures().length;
  }
}
